import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, ShieldAlert } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { openWhatsApp } from "@/lib/whatsapp";

const HERO_IMG = "/generated/hero-main.png";

const TITLE_LINES = ["Il medico a casa.", "Lo specialista", "ovunque ti trovi."];

const TRUST = [
  "Nessuna app da scaricare",
  "Infermiere qualificato a domicilio",
  "Medici generalisti e specialisti",
  "Prezzo comunicato prima della conferma",
];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.7 + i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const goToHow = () => navigate("/come-funziona");

  return (
    <section
      id="top"
      ref={ref}
      className="relative overflow-hidden bg-white pt-28 lg:pt-36 pb-24 lg:pb-24"
      data-testid="hero-section"
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-6">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em] text-[#5B2C91] shadow-[0_8px_30px_rgb(0,0,0,0.04)] ring-1 ring-[#5B2C91]/10"
              data-testid="hero-eyebrow"
            >
              <span className="h-2 w-2 rounded-full bg-[#D4F84A]" />
              Visite mediche assistite a domicilio
            </motion.span>

            <h1 className="font-heading mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1F152E]" data-testid="hero-title">
              {TITLE_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-2">
                  <motion.span
                    custom={i}
                    variants={lineVariants}
                    initial="hidden"
                    animate="visible"
                    className={`block ${i === 0 ? "" : "text-[#5B2C91]"}`}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#4A4059]"
              data-testid="hero-subtitle"
            >
              Un infermiere raggiunge il tuo domicilio con TytoCare. Il medico generalista o
              specialista si collega in diretta ed effettua una visita assistita con strumenti
              diagnostici connessi.
            </motion.p>

            {/* CTAs */}
            <motion.div
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate="visible"
              className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <button
                onClick={() => openWhatsApp()}
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-2.5 rounded-full bg-[#5B2C91] px-7 py-4 text-base font-semibold text-white shadow-[0_16px_40px_rgba(91,44,145,0.32)] hover:bg-[#4A2277] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
                data-testid="hero-whatsapp-btn"
              >
                <FaWhatsapp className="h-5 w-5 opacity-90" />
                Disponibilità su WhatsApp
              </button>
              <button
                onClick={goToHow}
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-base font-semibold text-[#5B2C91] ring-1 ring-[#5B2C91]/20 hover:ring-[#5B2C91]/40 hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200"
                data-testid="hero-how-btn"
              >
                Scopri come funziona
                <ArrowRight className="h-4 w-4" />
              </button>
            </motion.div>

            {/* Trust badges */}
            <motion.ul
              variants={fadeUp}
              custom={2}
              initial="hidden"
              animate="visible"
              className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3"
              data-testid="hero-trust-badges"
            >
              {TRUST.map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-[#3B2F4C]">
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#D4F84A]">
                    <Check className="h-3.5 w-3.5 text-[#1F152E]" strokeWidth={3} />
                  </span>
                  {t}
                </li>
              ))}
            </motion.ul>

            {/* Emergency note */}
            <motion.p
              variants={fadeUp}
              custom={3}
              initial="hidden"
              animate="visible"
              className="mt-7 mb-8 sm:mb-0 flex items-start gap-2 text-xs text-[#8A809A]"
              data-testid="hero-emergency-note"
            >
              <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-[#8A809A]" />
              Servizio dedicato a condizioni non emergenziali. In caso di emergenza contattare il
              112/118.
            </motion.p>
          </div>

          {/* RIGHT — image */}
          <div className="lg:col-span-6">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="relative z-10 overflow-hidden rounded-[2rem] lg:rounded-[2.5rem] shadow-[0_30px_80px_rgba(91,44,145,0.22)] ring-1 ring-white/60"
              >
                <motion.img
                  src={HERO_IMG}
                  alt="Infermiere a domicilio con TytoCare mentre il medico si collega in diretta sul tablet"
                  style={{ scale: imgScale }}
                  className="w-full h-[430px] sm:h-[520px] lg:h-[600px] object-cover"
                  data-testid="hero-image"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1F152E]/10 via-transparent to-transparent" />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
